<template>
    <div>
        <div class="content-header row">
            <div class="content-header-left col-md-6 col-12 mb-2">
                <h3 class="content-header-title mb-0">Signup Reports</h3>
                <div class="row breadcrumbs-top">
                    <div class="breadcrumb-wrapper col-12">
                        <ol class="breadcrumb">
                            <li class="breadcrumb-item"><Link href="/">Home</Link></li>
                            <li class="breadcrumb-item">Reports</li>
                            <li class="breadcrumb-item active">Signup Reports</li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>

        <div class="content-body">
            <div class="mb-2">
                <download-excel
                    ref="exporter-csv"
                    v-show="false"
                    type="csv"
                    :data="exportRows"
                    :name="generateReportName('Signup_Report', '.csv')"
                    sheet-name="Report"
                />
                <download-excel
                    ref="exporter-xls"
                    v-show="false"
                    :data="exportRows"
                    :name="generateReportName('Signup_Report', '.xls')"
                />
                <a href="#" class="export-link" @click.prevent="exportSignupReport('csv')">
                    <i class="fas fa-file-csv"></i> Export to CSV
                </a>
                |
                <a href="#" class="export-link text-warning" @click.prevent="exportSignupReport('xls')">
                    <i class="fa fa-file-excel-o"></i> Export to Excel
                </a>
            </div>

            <section class="card">
                <div class="card-header"><h4>Filters</h4></div>
                <div class="card-body">
                    <form @submit.prevent="getReport">
                        <div class="row">
                            <div class="col-xl-3 col-md-4 col-sm-6">
                                <label>Start date</label>
                                <date-time-field v-model="filter.start_date" class="form-control" bound="start"></date-time-field>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6">
                                <label>End date</label>
                                <date-time-field v-model="filter.end_date" class="form-control" bound="end"></date-time-field>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6">
                                <label>State</label>
                                <select v-model="filter.state_id" class="form-control" @change="onStateChange">
                                    <option value="">All states</option>
                                    <option v-for="state in states" :key="state.id" :value="state.id">{{ state.name }}</option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6">
                                <label>Store</label>
                                <select v-model="filter.store_id" class="form-control">
                                    <option value="">All stores</option>
                                    <option v-for="store in filteredStores" :key="store.id" :value="store.id">{{ store.name }}</option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>Signup Channel</label>
                                <select v-model="filter.signup_channel" class="form-control">
                                    <option value="">All channels</option>
                                    <option v-for="channel in channels" :key="channel.value" :value="channel.value">{{ channel.label }}</option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>Customer Type</label>
                                <select v-model="filter.customer_type" class="form-control">
                                    <option value="">All customers</option>
                                    <option value="new">New</option>
                                    <option value="returning">Returning</option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>Active/Inactive Customers</label>
                                <select v-model="filter.active_status" class="form-control">
                                    <option value="">All customers</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>&nbsp;</label><br>
                                <button type="submit" class="btn btn-primary" :disabled="loading">Filter</button>
                                <button type="button" class="btn btn-default" @click="resetFilter">Reset</button>
                            </div>
                        </div>
                    </form>
                </div>
            </section>

            <section class="card">
                <div class="card-body">
                    <p class="text-center"><b>{{ analytics.date_label }}</b></p>
                    <div class="row grouped-multiple-statistics-card">
                        <div class="col-lg-3 col-md-6 mb-2">
                            <h3 class="heading-text text-bold-600">{{ analytics.total_signups }}</h3>
                            <p class="sub-heading mb-0">Total Number of Signups</p>
                        </div>
                        <div class="col-lg-3 col-md-6 mb-2">
                            <h3 class="heading-text text-bold-600">{{ analytics.active_users }}</h3>
                            <p class="sub-heading mb-0">Active Users</p>
                        </div>
                        <div class="col-lg-3 col-md-6 mb-2">
                            <h3 class="heading-text text-bold-600">{{ analytics.total_paying_customers }}</h3>
                            <p class="sub-heading mb-0">Total Paying Customers</p>
                        </div>
                        <div class="col-lg-3 col-md-6 mb-2">
                            <h3 class="heading-text text-bold-600">{{ formatPercentage(analytics.retention_rate) }}</h3>
                            <p class="sub-heading mb-0">Customer Retention Rate</p>
                        </div>
                    </div>
                </div>
            </section>

            <section class="card p-3">
                <h4 class="content-header-title mb-2">Signups by Channel</h4>
                <BarChart v-if="signupsBar" :chart-data="signupsBar" :chart-options="{responsive: true}"/>
                <p v-else class="text-center mb-0">No signups for the selected filters.</p>
            </section>

            <section class="card p-3">
                <div class="row">
                    <div class="col-md-6 mt-1">
                        <h4 class="content-header-title mb-2">Signups vs Paying Customers</h4>
                        <PieChart v-if="signupPayingPie" :chart-data="signupPayingPie" :chart-options="{responsive: true}"/>
                        <p v-else class="text-center mb-0">No customer data for the selected filters.</p>
                    </div>
                    <div class="col-md-6 mt-1">
                        <h4 class="content-header-title mb-2">New vs Returning Customers</h4>
                        <PieChart v-if="customerTypePie" :chart-data="customerTypePie" :chart-options="{responsive: true}"/>
                        <p v-else class="text-center mb-0">No completed customer orders for the selected filters.</p>
                    </div>
                </div>
            </section>

        </div>
    </div>
</template>

<script>
import Layout from '@/Shared/Layout';
import {Link} from '@inertiajs/vue2';
import BarChart from '../../Shared/BarChart';
import PieChart from '../../Shared/PieChart';
import TableExportMixin from '../../Shared/Mixins/TableExportMixin';

export default {
    metaInfo: {title: 'Signup Reports'},
    layout: Layout,
    name: 'SignupReport',
    components: {BarChart, PieChart, Link},
    mixins: [TableExportMixin],
    props: {
        total_signups: {default: 0},
        signups_by_channel: {default: () => ({})},
        active_users: {default: 0},
        total_paying_customers: {default: 0},
        signups_vs_paying_customers: {default: () => ({})},
        new_vs_returning_customers: {default: () => ({})},
        customer_retention: {default: () => ({})},
        retention_rate: {default: 0},
        date_label: {default: ''},
        states: {default: () => []},
        stores: {default: () => []},
        channels: {default: () => []},
    },
    data() {
        return {
            loading: false,
            filter: {
                start_date: '',
                end_date: '',
                state_id: '',
                store_id: '',
                signup_channel: '',
                customer_type: '',
                active_status: '',
            },
            analytics: this.analyticsFromProps(),
            signupsBar: null,
            signupPayingPie: null,
            customerTypePie: null,
            exportRows: [],
        };
    },
    computed: {
        filteredStores() {
            if (!this.filter.state_id) return this.stores;
            return this.stores.filter(store => String(store.state_id) === String(this.filter.state_id));
        },
    },
    mounted() {
        this.updateCharts();
    },
    methods: {
        analyticsFromProps() {
            return {
                total_signups: this.total_signups,
                signups_by_channel: this.signups_by_channel,
                active_users: this.active_users,
                total_paying_customers: this.total_paying_customers,
                signups_vs_paying_customers: this.signups_vs_paying_customers,
                new_vs_returning_customers: this.new_vs_returning_customers,
                customer_retention: this.customer_retention,
                retention_rate: this.retention_rate,
                date_label: this.date_label,
            };
        },
        async getReport() {
            this.loading = true;
            try {
                const response = await axios.get(route('admin.reports.signup'), {
                    params: {...this.filter, api: true},
                    headers: {'X-Requested-With': 'XMLHttpRequest'},
                });
                this.analytics = response.data;
                this.updateCharts();
            } finally {
                this.loading = false;
            }
        },
        resetFilter() {
            Object.keys(this.filter).forEach(key => { this.filter[key] = ''; });
            this.getReport();
        },
        onStateChange() {
            this.filter.store_id = '';
        },
        updateCharts() {
            this.signupsBar = this.toBar(this.analytics.signups_by_channel, 'Number of signups');
            this.signupPayingPie = this.toPie(this.analytics.signups_vs_paying_customers);
            this.customerTypePie = this.toPie(this.analytics.new_vs_returning_customers);
        },
        toBar(values, label) {
            const labels = Object.keys(values || {});
            if (!labels.length) return null;
            return {
                labels,
                datasets: [{label, backgroundColor: '#00B5B8', data: labels.map(key => values[key])}],
            };
        },
        toPie(values) {
            const labels = Object.keys(values || {});
            const data = labels.map(key => Number(values[key]));
            if (!data.some(value => value > 0)) return null;
            return {
                labels,
                datasets: [{data, backgroundColor: ['#00B5B8', '#FFB74D', '#EF5350', '#5C6BC0']}],
            };
        },
        formatPercentage(value) {
            return `${Number(value || 0).toFixed(2)}%`;
        },
        generateReportName(name, fileType) {
            const now = new Date();
            return `${name}_${now.toISOString().slice(0, 19).replace(/[:T]/g, '-')}${fileType}`;
        },
        exportSignupReport(fileType) {
            this.exportReportData('admin.reports.signup', {}, this.filter, fileType);
        },
    },
};
</script>
